/**
 * useTocData — memoised table of content data derived from a doc-type skeleton.
 *
 * SRP: pure data derivation, no side effects, no Word.js.
 * Recomputes only when the skeleton reference changes — which happens once
 * on initial docConfig load. Stable across SSE updates and mid-run re-renders.
 */

import { useMemo } from "react";
import { type TocItem, buildTocTree, flattenToc } from "../toc";

export interface UseTocDataResult {
	/** Hierarchical tree for browse mode. Empty when skeleton is null. */
	tree: TocItem[];
	/** Flat pre-order list for search mode and keyboard navigation. */
	flat: TocItem[];
}

/**
 * @param skeleton - `[sectionNumber, title, wordTarget][]` from `docConfig.skeleton`.
 *   Pass `null` when no doc type is selected; returns empty tree + flat.
 */
export function useTocData(
	skeleton: [string, string, number][] | null,
): UseTocDataResult {
	const tree = useMemo(
		() => (skeleton ? buildTocTree(skeleton) : []),
		[skeleton],
	);
	const flat = useMemo(() => flattenToc(tree), [tree]);
	return { tree, flat };
}
